/**
 * Words as the voice path compares them (DCTA-141): lower case, no punctuation at either end, so an engine's capitals,
 * full stops and currency signs never make two equal words differ ("Hmm." = "hmm", "$50" = "50"). Shared by the
 * end-of-speech module (`app/end-of-speech.ts`) and the engines' adapters (`adapters/voice.ts`, and phase 2's
 * Tencent ASR), which all speak in pieces of one sentence.
 */
export function key(word: string): string {
  return word.toLowerCase().replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, "");
}

export function wordsOf(text: string): string[] {
  return text.trim() === "" ? [] : text.trim().split(/\s+/);
}

export function keysOf(text: string): string[] {
  return wordsOf(text).map(key).filter((w) => w !== "");
}

/**
 * Two pieces of one sentence, joined: what the new piece repeats of the old one's end is dropped (an engine that
 * re-hears the tail after a restart; Android's results that restate everything so far), compared word by word without
 * capitals or punctuation, the new piece's spelling kept. An engine's full stop at the seam goes ("to." + "my mum").
 */
export function stitch(before: string, after: string): string {
  const a = wordsOf(before);
  const b = wordsOf(after);
  if (a.length === 0) return b.join(" ");
  if (b.length === 0) return a.join(" ");
  for (let k = Math.min(a.length, b.length); k >= 1; k -= 1) {
    let same = true;
    for (let i = 0; i < k && same; i += 1) same = key(a[a.length - k + i] ?? "") === key(b[i] ?? "");
    if (same) return [...a.slice(0, a.length - k), ...b].join(" ");
  }
  const last = (a.at(-1) ?? "").replace(/(\.\.\.|…|\.)$/, "");
  return [...a.slice(0, -1), ...(last === "" ? [] : [last]), ...b].join(" ");
}
